import { useMutation } from '@tanstack/react-query';
import * as z from 'zod';

import { ALLOWED_DAYS, type MembershipGroup } from 'src/features/memberships/types';
import axios from 'src/lib/axios';
import { type MutationConfig } from 'src/lib/react-query';
import { type GeneralSmartParkError } from 'src/types';
import { validationMessagesMap } from 'src/utils/messages';
import { formatResourcePath } from 'src/utils/regex';

const RESOURCE = '/parkings/:parkingId/membership-groups/';

type FormSchemaValues = z.infer<typeof formSchema>;
const formSchema = z.object({
    name: z
        .string()
        .min(3, { message: validationMessagesMap.validationErrorName })
        .max(50, { message: validationMessagesMap.validationErrorName }),
    valid_range: z.object({
        from: z.coerce.date(),
        to: z.coerce.date(),
    }),
    allowed_hours_start: z.string().time(),
    allowed_hours_end: z.string().time(),
    allowed_points: z.array(z.string()).min(1, { message: validationMessagesMap.validationErrorRequired }),
    allowed_days: z
        .array(z.nativeEnum(ALLOWED_DAYS))
        .nonempty({ message: validationMessagesMap.validationErrorRequired }),
    allowed_parking_places: z.coerce.number().positive(),
    reserve_place: z.boolean(),
    allowed_parkings: z.array(z.string()).nonempty({ message: validationMessagesMap.validationErrorRequired }),
    description: z.string().optional().nullable(),
    allowed_group_points: z.array(z.string()).optional(),
});

interface DTO extends Omit<FormSchemaValues, 'valid_range'> {
    parkingId: string;
    valid_from: string;
    valid_until: string;
}

type MutationFnType = typeof mutationFn;
const mutationFn = ({ parkingId, ...data }: DTO): Promise<MembershipGroup> => {
    return axios.post(formatResourcePath(RESOURCE, { parkingId }), data);
};

interface UseMutationOptions extends MutationConfig<MutationFnType, GeneralSmartParkError> {}
const useMutationHook = (options: UseMutationOptions = {}) =>
    useMutation({
        mutationFn,
        ...options,
    });

export {
    RESOURCE as CREATE_MEMBERSHIP_GROUP_RESOURCE,
    useMutationHook as useCreateMembershipGroup,
    formSchema as createMembershipGroupFormSchema,
};
export type { DTO as CreateMembershipGroupDTO, FormSchemaValues as CreateMembershipGroupFormSchemaValues };
